import { CircularProgress } from "@mui/material";
import FormControl from "@mui/material/FormControl";
import FormControlLabel from "@mui/material/FormControlLabel";
import Radio from "@mui/material/Radio";
import RadioGroup from "@mui/material/RadioGroup";
import axios from "axios";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { useHistory, useLocation } from "react-router-dom";
import IconTitle from "../../components/commons/IconTitle";
import Modal from "../../components/commons/Modal";
import { handleErrorMessage } from "../../redux/actions/classes";
import {
  createCourse,
  editCourse
} from "../../redux/actions/courses";
import {
  editEcommerce
} from "../../redux/actions/groups";
import { validateFileSize } from "../../redux/constants/commonFunctions";
import { store } from "../../redux/store";
import CustomSelect from "./Components/CustomSelect";
import SignatureModal from "./Components/SignatureModal";
import styles from "./css/CreateCoursePage.module.css";
import ConfirmAddContentModal from "./Modals/ConfirmAddContentModal";

const CreateCoursePage = (props) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const history = useHistory();
  const location = useLocation();
  const uploadButtonRef = useRef();
  const isEditing = location?.state?.edit;
  const currentCourse = location?.state?.currentCourse;
  const infoIconSrc = "/Image/lecturePlan/quiz.svg";

  const courseType = location?.state?.courseType; // "Public" or "Private"

  const courseTitleType = location?.state?.courseTitleType; // "Create" or "Edit"
  const initialCourseMode = (() => {
    const fromLocation = location?.state?.courseMode;
    if (fromLocation) return fromLocation; // use what navigator passed
    // If we are in Edit mode and currentCourse is already present, infer mode from backend flag
    if (courseTitleType === "Edit" && currentCourse?.isLive !== undefined) {
      return currentCourse.isLive ? "live" : "recorded";
    }
    // default for Create flows
    return "recorded";
  })();

  const [courseModeState, setCourseModeState] = useState(initialCourseMode);

  // keep it in sync when editing and currentCourse arrives (runs only when id changes)
  useEffect(() => {
    if (courseTitleType === "Edit" && currentCourse?.id) {
      // only set if currentCourse has an explicit isLive boolean — avoids stomping user selection
      if (typeof currentCourse.isLive === "boolean") {
        setCourseModeState(currentCourse.isLive ? "live" : "recorded");
      }
    }
  }, [courseTitleType, currentCourse?.id]);

  // now use courseModeState where you used courseMode
  const courseMode = courseModeState;
  
  const [showFrequencyModal, setShowFrequencyModal] = useState(false);
  const [frequencyType, setFrequencyType] = useState(
    courseTitleType === "Edit" ? currentCourse?.type || "daily" : "daily"
  ); // 'daily' | 'weekly' | 'monthly'
  const [freqStartTime, setFreqStartTime] = useState(
    courseTitleType === "Edit" ? currentCourse?.start_time || "" : ""
  ); // "HH:MM"  

  const [freqWeeklyDays, setFreqWeeklyDays] = useState(
    courseTitleType === "Edit"
      ? currentCourse?.weekly_days ||
          {
            monday: false,
            tuesday: false,
            wednesday: false,
            thursday: false,
            friday: false,
            saturday: false,
            sunday: false,
          }
      : {
          monday: false,
          tuesday: false,
          wednesday: false,
          thursday: false,
          friday: false,
          saturday: false,
          sunday: false,
        }
  );
  const [freqChanged, setFreqChanged] = useState(false);
  const freqInitDone = useRef(false);
  const markFreqChanged = () => setFreqChanged(true);

  const updateFreqStartTime = (val) => {
    setFreqStartTime(val);
    markFreqChanged();
  };

  const updateStartDate = (val) => {
    setStartDate(val);
    markFreqChanged();
  };

  const updateRepeatEndDate = (val) => {
    setRepeatEndDate(val);
    markFreqChanged();
  };

  const updateFreqMonthlyDate = (val) => {
    setFreqMonthlyDate(val);
    markFreqChanged();
  };

  const toggleWeeklyDay = (day) => {
    setFreqWeeklyDays((p) => {
      const next = { ...p, [day]: !p[day] };
      return next;
    });
    markFreqChanged();
  };

  // returns "YYYY-MM-DD" for a Date-like input, or null if invalid
  const formatToYYYYMMDD = (d) => {
    if (!d) return null;
    // If it's already YYYY-MM-DD, return as-is
    if (/^\d{4}-\d{2}-\d{2}$/.test(d)) return d;
    // Try to parse as Date and convert to YYYY-MM-DD (UTC-based)
    const dt = new Date(d);
    if (isNaN(dt.getTime())) return null;
    return dt.toISOString().slice(0, 10);
  };
  
  const [freqMonthlyDate, setFreqMonthlyDate] = useState(
    courseTitleType === "Edit"
      ? formatToYYYYMMDD(currentCourse?.repeat_monthly_date) || ""
      : ""
  );

  const [startDate, setStartDate] = useState(
    courseTitleType === "Edit" ? currentCourse?.start_date ?? null : null
  );

  let startDateForBackend = null;
  const chosenStartDate = startDate || null;

  const [repeatEndDate, setRepeatEndDate] = useState(
    courseTitleType === "Edit" ? currentCourse?.repeat_until ?? null : null
  );

  let repeatUntilForBackend = null;
  const chosenEndDate = repeatEndDate || null;

  const [thumbnail, setThumbnail] = useState(
    courseTitleType == "Edit" && currentCourse?.thumbnail
  );
  const [isLoading, setIsLoading] = useState(false);

  const [previewFile, setPreviewFile] = useState(null);
  const previewInputRef = useRef(null);
  const modalFileInputRef = useRef(null);
  const [modalFilePreviewUrl, setModalFilePreviewUrl] = useState(null);

  const handlePreviewFile = (e) => {
    const f = e?.target?.files?.[0];
    if (!f) return;
    // optional: validate with validateFileSize(f, maxKb)
    setPreviewFile(f);
  };

  const [isLoadingSaveBtn, setIsLoadingSaveBtn] = useState(false);
  const [isLoadingNextBtn, setIsLoadingNextBtn] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);

  const [showPreviewModal, setShowPreviewModal] = useState(false);

  const [modalFile, setModalFile] = useState(null);
  const [modalLink, setModalLink] = useState("");

  const [previewLink, setPreviewLink] = useState(
    courseTitleType === "Edit" ? currentCourse?.preview_link ?? "" : ""
  );

  const [courseName, setCourseName] = useState(
    courseTitleType == "Edit" ? currentCourse?.name : ""
  );

  const [courseSubtitle, setCourseSubtitle] = useState(
    courseTitleType == "Edit" ? currentCourse?.subtitle : ""
  );

  const [courseLevel, setCourseLevel] = useState(
    courseTitleType == "Edit" ? currentCourse?.course_level : ""
  );

  const [timezone, setTimezone] = useState(
    courseTitleType === "Edit" ? currentCourse?.timezone : ""
  );

  const [courseDescription, setCourseDescription] = useState(
    courseTitleType == "Edit" ? currentCourse?.description : ""
  );

  const [prerequisites, setPrerequisites] = useState(
    courseTitleType == "Edit" ? (currentCourse?.prerequisites || []) : [""]
  );
  const [learningOutcomes, setLearningOutcomes] = useState(
    courseTitleType == "Edit" ? (currentCourse?.learning_outcomes || []) : ["", "", "", ""]
  );
  const [tags, setTags] = useState(
    courseTitleType == "Edit" ? (currentCourse?.tags || []) : []
  );
  const [tagInput, setTagInput] = useState("");

  const [showSignatureModal, setShowSignatureModal] = useState(false);
  const [signature, setSignature] = useState(null);
  const [signatureFile, setSignatureFile] = useState(null);

  const [isFree, setIsFree] = useState(
    courseTitleType == "Edit" ? !!currentCourse?.free : true
  );

  const [coursePriceValue, setCoursePriceValue] = useState(
    courseTitleType == "Edit" ? currentCourse?.price ?? "" : ""
  );

  const [courseDiscountedPrice, setCourseDiscountedPrice] = useState(
    courseTitleType == "Edit" ? currentCourse?.discounted_price ?? "" : ""
  );

  const [paymentLink, setPaymentLink] = useState(
    courseTitleType == "Edit" ? currentCourse?.payment_link ?? "" : ""
  );

  const [liveClassUrl, setLiveClassUrl] = useState(
    courseTitleType == "Edit"
      ? currentCourse?.meeting_link ?? (currentCourse?.urls?.[0]?.url ?? "")
      : ""
  );

  const title = `${
    courseTitleType == "Create" ? t("common.create") : t("common.edit")
  } ${t( "common.course" )}`;

  const profileId = useSelector((state) => state?.profile?.profileId) || 0;
  // const classes = useSelector((state) => state.classes.classes).filter(
  //   (item) => item.class_owner == profileId
  // );
  const classes = location?.state?.classes;

  const classTags = props.classTags ?? [];
  const classLoggedIn = useSelector((state) => state.classes.class);
  const currentClass =
    props.courseFeedCourse === true ? [classLoggedIn.name] : [];

  const [selectedClasses, setSelectedClasses] = useState(
    courseTitleType === "Edit"
      ? currentCourse?.classes?.map((item) => item.name)
      : currentClass
  );

  const requiresLiveUrl = courseMode === "live";

  const buildISOFromDateAndTime = (dateStr, timeStr) => {
    // If no dateStr, default to today's date (local)
    const pad = (n) => String(n).padStart(2, "0");
    const today = new Date();
    const defaultDate = `${today.getFullYear()}-${pad(today.getMonth() + 1)}-${pad(today.getDate())}`;
    const datePart = dateStr || defaultDate;
    // If no time provided, use midnight (00:00)
    const timePart = timeStr || "00:00";
    // Construct local datetime then convert to ISO UTC string (includes Z)
    const local = new Date(`${datePart}T${timePart}:00`);
    return isNaN(local.getTime()) ? null : local.toISOString(); // e.g. "2025-09-05T18:00:00.000Z"
  };

  const weekdayKeyMap = {
    monday: "mon",
    tuesday: "tue",
    wednesday: "wed",
    thursday: "thu",
    friday: "fri",
    saturday: "sat",
    sunday: "sun",
  };

  const reverseWeekdayKeyMap = {
    mon: "monday",
    tue: "tuesday",
    wed: "wednesday",
    thu: "thursday",
    fri: "friday",
    sat: "saturday",
    sun: "sunday",
  };

  const uploadSignature = async (file) => {
    if (!file) return;

    const token = store.getState()?.profile?.user?.token;
    const userData = JSON.parse(localStorage.getItem("userData"));
    const profileId = userData?.profile_id;

    if (!profileId) {
      console.error("No profile_id found in localStorage.userData");
      return;
    }

    const formData = new FormData();
    formData.append("signature", file);

    try {
      await axios.put(
        `${process.env.REACT_APP_API_BASE_URL}api/v1/profile/${profileId}/`,
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
            Authorization: `Token ${token}`,
          },
        }
      );
      console.log("Signature uploaded successfully");
    } catch (err) {
      console.error("Signature upload failed:", err);
    }
  };

  async function createDefaultThumbnailFile({
    src = '/Image/CourseMonetisation/course-default-image.png',
    width = 1000,
    height = 700,
    filename = 'course-default-image.png',
    mime = 'image/png',
    overlay = {
      color: 'rgba(243,48,112,',
      stops: [0.03, 0.05, 0.08, 0.12],
    },
  } = {}) {
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.src = src;

    await new Promise((resolve, reject) => {
      img.onload = resolve;
      img.onerror = (e) => reject(new Error('Failed to load default image: ' + src));
    });

    const canvas = document.createElement('canvas');
    canvas.width = width;
    canvas.height = height;
    const ctx = canvas.getContext('2d');

    const imgRatio = img.width / img.height;
    const canvasRatio = width / height;
    let drawW, drawH, drawX, drawY;
    if (imgRatio > canvasRatio) {
      drawH = height;
      drawW = img.width * (height / img.height);
      drawX = (width - drawW) / 2;
      drawY = 0;
    } else {
      drawW = width;
      drawH = img.height * (width / img.width);
      drawX = 0;
      drawY = (height - drawH) / 2;
    }

    ctx.drawImage(img, drawX, drawY, drawW, drawH);

    const grad = ctx.createRadialGradient(
      width / 2,
      height / 2,
      0,
      width / 2,
      height / 2,
      Math.max(width, height) * 0.8
    );

    // stops based on overlay.stops (alpha values)
    grad.addColorStop(0, `${overlay.color}${overlay.stops[0]})`);
    grad.addColorStop(0.45, `${overlay.color}${overlay.stops[1]})`);
    grad.addColorStop(0.7, `${overlay.color}${overlay.stops[2]})`);
    grad.addColorStop(1, `${overlay.color}${overlay.stops[3]})`);

    ctx.fillStyle = grad;
    ctx.globalCompositeOperation = 'source-over';
    ctx.fillRect(0, 0, width, height);

    const blob = await new Promise((resolve) =>
      canvas.toBlob((b) => resolve(b), mime, 0.92)
    );

    const file = new File([blob], filename, { type: blob.type });

    return file;
  }

  const mapWeeklyDaysToBackend = (freqWeeklyDaysObj) => {
    if (!freqWeeklyDaysObj) return null;
    const keys = Object.keys(freqWeeklyDaysObj).filter((k) => !!freqWeeklyDaysObj[k]);
    if (!keys.length) return null;
    return keys.map((k) => weekdayKeyMap[k]).filter(Boolean);
  };

  const extractTimeFromISO = (iso) => {
    if (!iso) return "";
    try {
      const d = new Date(iso);
      const pad = (n) => String(n).padStart(2, "0");
      return `${pad(d.getHours())}:${pad(d.getMinutes())}`;
    } catch (e) {
      return "";
    }
  };

  const initFreqWeeklyDaysFromBackend = (backendArray) => {
    const base = {
      monday: false, tuesday: false, wednesday: false, thursday: false,
      friday: false, saturday: false, sunday: false
    };
    if (!Array.isArray(backendArray)) return base;
    backendArray.forEach((short) => {
      const k = reverseWeekdayKeyMap[short];
      if (k) base[k] = true;
    });
    return base;
  };

  // minimal validator (true if already in YYYY-MM-DD)
  const isValidYYYYMMDD = (s) => !!s && /^\d{4}-\d{2}-\d{2}$/.test(s);

  useEffect(() => {
    if (courseTitleType === "Edit" && currentCourse) {
      // frequencyType should come from backend 'repeat'
      if (currentCourse.repeat) setFrequencyType(currentCourse.repeat);

      // start_time — parse into HH:MM for <input type="time" />
      if (currentCourse.start_time) {
        setFreqStartTime(extractTimeFromISO(currentCourse.start_time));
      } else {
        setFreqStartTime("");
      }

      // repeat_weekdays might be ["mon","wed"] — convert to your checkbox object
      if (currentCourse.repeat_weekdays && Array.isArray(currentCourse.repeat_weekdays)) {
        setFreqWeeklyDays(initFreqWeeklyDaysFromBackend(currentCourse.repeat_weekdays));
      } else if (currentCourse.weekly_days && typeof currentCourse.weekly_days === "object") {
        // backward-compat: if backend gave object already use it
        setFreqWeeklyDays(currentCourse.weekly_days);
      }

      // Normalize repeat_until and repeat_monthly_date into YYYY-MM-DD
      if (currentCourse.repeat_until) {
        setRepeatEndDate(formatToYYYYMMDD(currentCourse.repeat_until) || null);
      } else {
        setRepeatEndDate(null);
      }

      const monthlyDateNormalized =
        currentCourse.repeat_monthly_date
          ? formatToYYYYMMDD(currentCourse.repeat_monthly_date) || ""
          : "";
      setFreqMonthlyDate(monthlyDateNormalized);
      setPrerequisites(currentCourse.prerequisites || []);
      setLearningOutcomes(currentCourse.learning_outcomes || []);
      setTags(currentCourse.tags || []);
      setTimezone(currentCourse?.timezone ?? "ist");
      setCourseLevel(currentCourse?.course_level ?? "all");
    } else {
      setFreqMonthlyDate("");
      setRepeatEndDate(null);
      setFreqWeeklyDays({
        monday: false, tuesday: false, wednesday: false, thursday: false,
        friday: false, saturday: false, sunday: false
      });
      setFreqStartTime("");
    }
    freqInitDone.current = true;
    setFreqChanged(false);

    if (courseTitleType === "Edit") {
      setFreqChanged(true);
    }
  }, [
    courseTitleType,
    currentCourse?.id,
    currentCourse?.repeat_monthly_date,
    currentCourse?.monthly_date,
    currentCourse?.repeat_until,
    currentCourse?.start_time,
    currentCourse?.repeat_weekdays,
    currentCourse?.weekly_days,
    currentCourse?.repeat,
  ]);

  // helpers for prerequisites/outcomes/tags
  const updateArrayItem = (arrSetter) => (idx, val) => {
    arrSetter((prev) => {
      const next = [...prev];
      next[idx] = val;
      return next;
    });
  };
  const removeArrayItem = (arrSetter) => (idx) => {
    arrSetter((prev) => prev.filter((_, i) => i !== idx));
  };
  const addArrayItem = (arrSetter) => () => {
    arrSetter((prev) => [...prev, ""]);
  };
  const addTag = () => {
    const v = (tagInput || "").trim();
    if (!v) return;
    if (!tags.includes(v)) setTags((p) => [...p, v]);
    setTagInput("");
  };
  const removeTag = (idx) => {
    setTags((p) => p.filter((_, i) => i !== idx));
  };

  const normalizeUrl = (raw) => {
    if (!raw) return "";
    const s = String(raw).trim();
    if (s === "") return "";
    if (/^[a-zA-Z][a-zA-Z0-9+.-]*:\/\//.test(s) || /^[a-zA-Z][a-zA-Z0-9+.-]*:/.test(s)) {
      return s;
    }
    if (s.startsWith("//")) return "https:" + s;
    return "https://" + s;
  };

  useEffect(() => {
    if (!modalFile) {
      setModalFilePreviewUrl(null);
      return;
    }
    const url = URL.createObjectURL(modalFile);
    setModalFilePreviewUrl(url);
    return () => {
      URL.revokeObjectURL(url);
    };
  }, [modalFile]);

  const handleModalFilePicked = (file) => {
    const MAX_BYTES = 25 * 1024 * 1024;
    if (!file) return;
    if (file.size > MAX_BYTES) {
      alert("File is too large. Max 25 MB allowed.");
      return;
    }
    setModalFile(file);
  };

  // When user switches frequency, reset/disable other fields as required:
  const emptyWeekly = {
    monday: false,
    tuesday: false,
    wednesday: false,
    thursday: false,
    friday: false,
    saturday: false,
    sunday: false,
  };

  const handleFrequencyChange = (newType) => {
   // newType may be 'daily' | 'weekly' | 'monthly' | 'none' (one-time)
    setFrequencyType(newType);
    markFreqChanged();

    if (newType === "daily") {
      // daily -> clear monthly & weekly-specific values
      setFreqWeeklyDays(emptyWeekly);
      setFreqMonthlyDate("");
    } else if (newType === "weekly") {
      // weekly -> clear monthly-specific value
      setFreqMonthlyDate("");
      // keep weekly days as-is
    } else if (newType === "monthly") {
      // monthly -> clear weekly selection
      setFreqWeeklyDays(emptyWeekly);
    } else if (newType === "none") {
      // one-time -> not a repeating frequency; clear weekly/monthly helper values
      setFreqWeeklyDays(emptyWeekly);
      setFreqMonthlyDate("");
      // repeatEndDate will hold the one-time date
    }
  };

  const handleDeleteOrClear = (arr, arrSetter, idx, minCount) => {
    const len = (arr?.length || 0);
    if (len > minCount) {
      // remove the whole field
      removeArrayItem(arrSetter)(idx);
    } else {
      // just clear the content of the field (leave the field present)
      updateArrayItem(arrSetter)(idx, "");
    }
  };

  // Updated button disabled condition to include thumbnail requirement
  const isBtnDisabled =
    !courseName ||
    !courseSubtitle ||
    !courseLevel ||
    !courseDescription ||
    !signatureFile ||
    (requiresLiveUrl && !timezone) ||
    (!isFree && String(coursePriceValue) === "")||
    (requiresLiveUrl && !liveClassUrl);

  // useEffect(() => {
  //   return () => {
  //     localStorage.removeItem("currentCourse");
  //     dispatch(clearCourses());
  //   };
  // }, []);

  const getSelectedClasses = (value) => {
    setSelectedClasses(value);
  };

  const handleThumbnail = (e) => {
    let errorMessage = validateFileSize(e.target.files[0], 5120);
    if (errorMessage) {
      dispatch(handleErrorMessage(errorMessage));
    } else {
      setThumbnail(e.target.files[0]);
      // setThumbnailUrl(URL.createObjectURL(e.target.files[0]));
    }
  };

  const handleEdit = () => {
    const obj = {
      id: currentCourse.id,
      name: courseName,
      value: coursePriceValue,
      sub_title: courseSubtitle,
      course_level: courseLevel,
      description: courseDescription,
    };

    dispatch(editEcommerce(obj));
  };

  // New One
  const handleNextAndSave = async (btnType) => {
    setIsLoadingNextBtn(true);
    setIsLoadingSaveBtn(true);
    const token = store.getState()?.profile?.user?.token;

    const formData = new FormData();
    const isEdit = courseTitleType === "Edit";

    if (courseMode === "live") {
      if (!freqStartTime) {
        dispatch(handleErrorMessage(t("createCourse.pleaseSelectStartTime") || "Please select start time"));
        setIsLoadingNextBtn(false);
        setIsLoadingSaveBtn(false);
        return;
      }
    }

    if (chosenStartDate) {
      const normalized = formatToYYYYMMDD(chosenStartDate);
      startDateForBackend = normalized || null;
    } else {
      startDateForBackend = null;
    }

    if (chosenEndDate) {
      const normalized = formatToYYYYMMDD(chosenEndDate);
      repeatUntilForBackend = normalized || null;
    } else {
      repeatUntilForBackend = null;
    }


    let baseDate = null;
    if (isEdit && currentCourse?.start_time) {
      // currentCourse.start_time may be "2025-09-01T18:00:00Z" -> extract date part
      try {
        baseDate = currentCourse.start_time.split("T")[0];
      } catch (e) { baseDate = null; }
    }
    if (!baseDate && frequencyType === "monthly" && freqMonthlyDate) {
      baseDate = freqMonthlyDate; // user-supplied "YYYY-MM-DD"
    }
    // otherwise buildISOFromDateAndTime will fallback to today if date missing

    const startISOLive = buildISOFromDateAndTime(baseDate, freqStartTime); // may be null

    const nowIso = () => new Date().toISOString();

    // If user selected monthly and supplied freqMonthlyDate, use that first (this ensures edits are respected)
    if (frequencyType === "monthly" && freqMonthlyDate) {
      baseDate = freqMonthlyDate;
    } else if (isEdit && currentCourse?.start_time) {
      // fallback to existing course start_time if user didn't provide a monthly date
      try {
        baseDate = currentCourse.start_time.split("T")[0];
      } catch (e) {
        baseDate = null;
      }
    }

    const startISO = courseMode === "live" ? (startISOLive ?? nowIso()) : nowIso();
    
    const repeatMonthlyForBackend =
      frequencyType === "monthly" && freqMonthlyDate
        ? buildISOFromDateAndTime(freqMonthlyDate, freqStartTime) // uses freqStartTime if available
        : null;

    // Map weekly object -> ["mon","tue"] or null
    const repeatWeekdaysForBackend = mapWeeklyDaysToBackend(freqWeeklyDays);

    // Repeat type mapping: ensure default 'none' when not set
    const repeatForBackend = frequencyType || "none";

    // repeat_until: currently you store freqMonthlyDate (YYYY-MM-DD) — send that or null

    const operations = {
      query: `
     mutation ($input: CreateCourseInput!) {
    createcourse(input: $input) {
      id
      thumbnail
      preview_video
      preview_video_link
      name
      description
      subtitle
      public
      owner {
        name
        id
        profile_link
        totalCourses
        totalLearners
        signature
        social_links
        short_bio
      }
      user_profile {
        id
        name
        profile_pic
      }
      files {
        id
        course_id
        media
      }
      urls {
        url
        id
      }
      active
      free
      price
      currency
      members_count
      classes {
        id
        name
      }
      is_enrolled
      section {
        id
        title
        lectures {
          id
          title
          description
          fileLink
          url
          fileType
          article
          duration
          completed
        }
        total_lecs
        sec_mins
        quiz {
          id
          title
          details
          max_marks
          template {
            ques_count
          }
        }
      }
      course_mins
      total_lecs
      progress_bar
      course_level
      is_draft
      payment_link
      meeting_link
      start_time
      repeat
      start_date
      repeat_until
      repeat_weekdays
      repeat_monthly_date
      isLive
      prerequisites
      learning_outcomes
      tags
      timezone
    }
}
      `,
      variables: {
        input: {
          name: courseName,
          description: courseDescription,
          subtitle: courseSubtitle || "",
          free: isEdit ? currentCourse?.free : isFree,
          public: courseType==='Public'?true:false,
          currency: "usd",
          price: isFree ? 0 : Number(coursePriceValue),
          discounted_price: isFree ? 0 : (courseDiscountedPrice === "" || courseDiscountedPrice === null ? null : Number(courseDiscountedPrice)),
          active: isEdit ? currentCourse?.active : true,
          is_draft: true,
          thumbnail: null,
          course_level: courseLevel || "",
          payment_link: paymentLink || "",
          isLive: courseMode === "live",
          meeting_link: liveClassUrl ?? "",
          start_time: startISO,
          repeat: repeatForBackend ?? "",
          start_date: startDateForBackend ?? null,
          repeat_until: repeatUntilForBackend ?? null,
          repeat_weekdays: repeatWeekdaysForBackend, // ["mon","tue"] or null
          repeat_monthly_date: repeatMonthlyForBackend ?? null,
          prerequisites: prerequisites && prerequisites.length ? prerequisites : null,
          learning_outcomes: learningOutcomes && learningOutcomes.length ? learningOutcomes : null,
          tags: tags && tags.length ? tags : null,
          timezone: timezone,
        },
      },
    };

    operations.variables.input.preview_video_link = previewLink ?? null;

    if (previewFile) {
    operations.variables.input.preview_video = null;
    delete operations.variables.input.preview_video_link;
  } else if (previewLink) {
    operations.variables.input.preview_video_link = previewLink;
    delete operations.variables.input.preview_video;
  }


    formData.append("operations", JSON.stringify(operations));

    let fileToUpload = null;
    if (thumbnail && thumbnail instanceof File) {
      fileToUpload = thumbnail;
    } else {
      try {
        fileToUpload = await createDefaultThumbnailFile({
          src: "/Image/CourseMonetisation/course-default-image.png",
          width: 1000,
          height: 700,
          filename: "course-default-image-with-overlay.png",
          mime: "image/png",
        });
      } catch (err) {
        console.error("Failed to generate default thumbnail file:", err);
        fileToUpload = null;
      }
    }

    const fileMap = {};
    const filesToAppend = [];
    let mapIndex = 1;

    if (fileToUpload) {
      fileMap[String(mapIndex)] = ["variables.input.thumbnail"];
      filesToAppend.push({ key: String(mapIndex), file: fileToUpload });
      mapIndex++;
    }

    if (previewFile) {
      fileMap[String(mapIndex)] = ["variables.input.preview_video"];
      filesToAppend.push({ key: String(mapIndex), file: previewFile });
      mapIndex++;
    }

    if (Object.keys(fileMap).length > 0) {
      formData.append("map", JSON.stringify(fileMap));
      filesToAppend.forEach((f) => {
        formData.append(f.key, f.file, f.file.name);
      });
    }

    if (courseTitleType === "Edit") {
      const hasPreviewFile = previewFile instanceof File;
      const hasThumbnailFile = thumbnail instanceof File;
      const editData = {
        courseId: currentCourse?.id,
        classes: selectedClasses,
        courseName: courseName,
        courseSubtitle: courseSubtitle,
        course_level: courseLevel,
        courseDescription: courseDescription,
        price: isFree ? 0 : Number(coursePriceValue),
        discounted_price: isFree ? 0 : (courseDiscountedPrice === "" || courseDiscountedPrice === null ? null : Number(courseDiscountedPrice)),
        payment_link: paymentLink ?? "",
        meeting_link: liveClassUrl ?? "",
        active: currentCourse?.active ?? true,
        start_time: startISO,
        repeat: repeatForBackend ?? "",
        start_date: startDateForBackend ?? null,
        repeat_until: repeatUntilForBackend ?? null,
        repeat_weekdays: repeatWeekdaysForBackend,
        repeat_monthly_date:
        frequencyType === "monthly"
          ? (repeatMonthlyForBackend ?? (isValidYYYYMMDD(freqMonthlyDate) ? buildISOFromDateAndTime(freqMonthlyDate, freqStartTime) : null))
          : null,
        prerequisites: prerequisites && prerequisites.length ? prerequisites : null,
        learning_outcomes: learningOutcomes && learningOutcomes.length ? learningOutcomes : null,
        tags: tags && tags.length ? tags : null,
        timezone: timezone,
      };

      // Always include preview link when editing (if present)
      editData.preview_video_link = previewLink ?? null;

      // If user selected a new preview file while editing, do a multipart GraphQL update
      if (hasPreviewFile || hasThumbnailFile) {
        // construct multipart update for preview file
        const GRAPHQL_ENDPOINT = process.env.REACT_APP_GRAPHQL_BASE_URL;

        const updateOps = {
          query: `
            mutation ($input: UpdateCourseInput!) {
              updatecourse(input: $input) {
                id
                preview_video_link
                thumbnail
              }
            }
          `,
          variables: {
            input: {
              id: currentCourse?.id,
              preview_video_link: previewLink ?? null,
              // include any other editable fields here if required
            },
          },
        };

        if (hasPreviewFile) updateOps.variables.input.preview_video = null; // placeholder for multipart
        if (hasThumbnailFile) updateOps.variables.input.thumbnail = null;   //

        const fd = new FormData();
        fd.append("operations", JSON.stringify(updateOps));

        // Build map and append files
        const map = {};
        let idx = 1;
        if (hasThumbnailFile) {
          map[String(idx)] = ["variables.input.thumbnail"];
          fd.append(String(idx), thumbnail, thumbnail.name);
          idx++;
        }
        if (hasPreviewFile) {
          map[String(idx)] = ["variables.input.preview_video"];
          fd.append(String(idx), previewFile, previewFile.name);
          idx++;
        }

        fd.append("map", JSON.stringify(map));

        try {
          const res = await fetch(GRAPHQL_ENDPOINT, {
            method: "POST",
            headers: {
              Authorization: `Token ${token}`,
              Accept: "*/*",
            },
            body: fd,
          });
          const json = await res.json();
          // optional: handle json errors or refresh local course state
        } catch (err) {
          console.error("Edit file upload failed:", err);
          handleErrorMessage(err);
        }

        // After file upload, still dispatch editCourse to update other fields (if needed)
        try {
          dispatch(editCourse(editData)).then(() => {
            if (btnType === "next" && currentCourse?.id) {
              setIsLoadingNextBtn(false);
              history.push({
                pathname: `/course/content/${currentCourse?.id}`,
              });
            }
          });
        } catch (error) {
          handleErrorMessage(error);
          console.error("Edit error:", error);
        }
      } else {
        try {
          dispatch(editCourse(editData)).then(() => {
            if (btnType === "next" && currentCourse?.id) {
              setIsLoadingNextBtn(false);
              history.push({
                pathname: `/course/content/${currentCourse?.id}`,
              });
            }
          });
        } catch (error) {
          handleErrorMessage(error);
          console.error("Edit error:", error);
        }
      }
    } else {
      if (!isFree) {
        if (!validatePrice(coursePriceValue)) {
          setIsLoadingNextBtn(false);
          setIsLoadingSaveBtn(false);
          return;
        }
        if (!validateDiscount(coursePriceValue, courseDiscountedPrice)) {
          setIsLoadingNextBtn(false);
          setIsLoadingSaveBtn(false);
          return;
        }
      }
      try {
        dispatch(createCourse(formData, "academics")).then(async() => {
          const cc = JSON.parse(localStorage.getItem("currentCourse"));
          if (signatureFile) {
            await uploadSignature(signatureFile);
          }
          if (btnType === "next" && cc?.id) {
            setIsLoadingNextBtn(false);
            history.push({
              pathname: `/course/content/${cc.id}`,
            });
          }
          if (btnType === "save") {
            setIsLoadingSaveBtn(false);
            history.push({
              pathname: `/courses`,
            });
          }
        });
      } catch (error) {
        handleErrorMessage(error);
        console.error("Upload error:", error);
      }
    }
  };

  // dpn't remove this
  // const handleNextAndSave = async (btnType) => {
  //   // if (file?.size) {
  //   // setLoading(true)

  //   // const formData = new FormData();
  //   // formData.append('product_service_type', 'PRIVATE');
  //   // // formData.append('thumbnail', thumbnail);
  //   // formData.append('group_id', '58');
  //   // // formData.append('cover_image', '');
  //   // formData.append('name', courseName);
  //   // formData.append('description', courseDescription);
  //   // formData.append('free', true);
  //   // formData.append('value', '10000');
  //   // formData.append('discountedPrice', '100');

  //   const data = {
  //     product_service_type: "PRIVATE",
  //     thumbnail: thumbnail,
  //     //       // classes: selectedClasses
  //     //       //   ?.map((e) => classes.find((item) => item.name === e))
  //     //       //   ?.map((x) => x?.id),
  //     name: courseName,
  //     group_id: store?.getState()?.groups?.group?.id,
  //     sub_title: courseSubtitle,
  //     description: courseDescription,
  //     free: courseTitleType == "Edit" ? currentCourse?.free : isFree,
  //     value: isFree ? 0 : Number(coursePriceValue),
  //     discounted_price: isFree ? 0 : Number(courseDiscountedPrice),
  //     active: courseTitleType == "Edit" ? currentCourse?.active : true,
  //   };
  //   // console.log('line149', data)
  //   //       const token = store.getState()?.profile?.user?.token;
  //   // // const authToken = token;
  //   dispatch(createProductService(data)).then(() => {
  //     dispatch(handleSuccessMessage("Created"));
  //     // history.push({
  //     //   pathname: `/group/${store?.getState()?.groups?.group?.id}`,

  //     // });
  //     // history.push({
  //     //   pathname: `/group/${store?.getState()?.groups?.group?.id}`,
  //     //   state: { activeSection: "Courses" },
  //     // });
  //   });
  //   // createProductService(formData)
  //   //   const token = store.getState()?.profile?.user?.token;
  //   //   const config = {
  //   //       headers: {
  //   //           'content-type': 'application/json',
  //   //           Authorization: `Token ${token}`,
  //   //       },
  //   //   };
  //   //   const data={
  //   //           product_service_type: 'PRIVATE',
  //   //           thumbnail: thumbnail,
  //   //           group_id:'58',
  //   //           cover_image:'',
  //   //     //       // classes: selectedClasses
  //   //     //       //   ?.map((e) => classes.find((item) => item.name === e))
  //   //     //       //   ?.map((x) => x?.id),
  //   //           name: courseName,
  //   //           sub_title: courseSubtitle,
  //   //           description: courseDescription,
  //   //           free: courseTitleType == "Edit" ? currentCourse?.free : isFree,
  //   //           value: isFree ? 0 : Number(coursePriceValue),
  //   //           discountedPrice:courseDiscountedPrice,
  //   //       //     // active: courseTitleType == "Edit" ? currentCourse?.active : true,
  //   //         }
  //   //         console.log('169',data)
  //   //  await axios({
  //   //     method: "post",
  //   //     url: 'https://liquidapi.stage.nxnearby.com/api/v1/productservice/',
  //   //     data: formData,
  //   //     headers: {
  //   //       "content-type": "multipart/form-data",
  //   //       Authorization: `Token ${token}`,
  //   //     },
  //   //   })

  //   // axios.post('https://liquidapi.stage.nxnearby.com/api/v1/productservice', data, config).then((response) => {
  //   //     console.log(response.data);

  //   // })
  //   //     .catch(e => {
  //   //         console.log(e);
  //   //     })
  // };

  // const handleNextAndSave = (btnType) => {
  //   console.log("camee");
  //   const data = {
  //     product_service_type: "PRIVATE",
  //     thumbnail: thumbnail,
  //     // classes: selectedClasses
  //     //   ?.map((e) => classes.find((item) => item.name === e))
  //     //   ?.map((x) => x?.id),
  //     sub_title: courseSubtitle,
  //     description: courseDescription,
  //     free: courseTitleType == "Edit" ? currentCourse?.free : isFree,
  //     value: isFree ? 0 : Number(coursePriceValue),
  //     discountedPrice: courseDiscountedPrice,
  //     active: courseTitleType == "Edit" ? currentCourse?.active : true,

  //     name: courseName,
  // //         group_id: store?.getState()?.groups?.group?.id,
  // //         sub_title: courseSubtitle,
  // //         description: courseDescription,
  // //         free: courseTitleType == "Edit" ? currentCourse?.free : isFree,
  // //         value: isFree ? 0 : Number(coursePriceValue),
  // //         discounted_price: isFree ? 0 : Number(courseDiscountedPrice),
  // //         active: courseTitleType == "Edit" ? currentCourse?.active : true,
  // //         thumbnail: null, // placeholder, will be replaced using `map`
  //   };
  //   console.log("inputData", data);

  //   if (
  //     data?.courseName?.trim() == "" ||
  //     data?.courseSubtitle?.trim() == "" ||
  //     data?.courseDescription?.trim() == "" ||
  //     data?.courseType === "Private" ||
  //     (!isFree && coursePriceValue === "")
  //   ) {
  //     // console.log('line 123',data)
  //     dispatch(
  //       handleErrorMessage(t("errorMessageTranslations.requiredFields"))
  //     );
  //     setIsLoadingNextBtn(false);
  //     setIsLoadingSaveBtn(false);
  //   } else if (!isFree && !validatePrice(data?.priceValue)) {
  //     setIsLoadingNextBtn(false);
  //     setIsLoadingSaveBtn(false);
  //   } else if (courseTitleType == "Edit") {
  //     data.id = currentCourse?.id;

  //     if (thumbnail) {
  //       data.thumbnail = thumbnail;
  //     }

  //     dispatch(updateCourse(data, currentCourse?.is_draft)).then(() => {
  //       const cc = JSON.parse(localStorage.getItem("currentCourse"));
  //       if (btnType == "next" && cc?.id) {
  //         setIsLoadingNextBtn(false);
  //         history.push({
  //           pathname: `/course/content/${cc.id}`,
  //         });
  //       } else {
  //         setIsLoadingNextBtn(false);
  //         history.push({
  //           pathname: `/academics`,
  //           state: { switchSection: "course" },
  //         });
  //       }
  //     });
  //   }

  //   try {
  //     axios
  //       .post(
  //         "https://liquidapi.stage.nxnearby.com/api/v1/productservice",
  //         data,
  //         {
  //           header: {
  //             Authentication: "Bearer 55d6cdf5676d0eeb56e9e29d44bb369594476570",
  //           },
  //         }
  //       )
  //       .then((res) => console.log(res));
  //   } catch (error) {
  //     console.log(error);
  //   }

  //   dispatch(createCourse(data)).then(() => {
  //     const cc = JSON.parse(localStorage.getItem("currentCourse"));
  //     if (btnType == "next" && cc?.id) {
  //       setIsLoadingNextBtn(false);
  //       history.push({
  //         pathname: `/course/content/${cc.id}`,
  //       });
  //     } else {
  //       setIsLoadingSaveBtn(false);
  //       history.push({
  //         pathname: `/academics`,
  //         state: { switchSection: "course" },
  //       });
  //     }
  //   });
  // };

  const validatePrice = (price) => {
    let errorMessage = null;
    switch (price) {
      case null:
      case undefined:
      case "":
        errorMessage = "errorMessageTranslations.enterValidNumber";
        break;
      default:
        if (!isNaN(price)) {
          if (price < 0) {
            errorMessage = "courseMonetisation.priceCantBeNegative";
          } else if (price === 0) {
            errorMessage = "courseMonetisation.priceCantBeZero";
          }
        } else {
          errorMessage = "errorMessageTranslations.enterValidNumber";
        }
    }

    if (errorMessage) {
      dispatch(handleErrorMessage(t(errorMessage)));
      return false;
    } else {
      return true;
    }
  };

  // Validate discount (returns true when OK)
  const validateDiscount = (price, discounted) => {
    const p = Number(price);
    const d = Number(discounted);
    if (discounted === "" || discounted === null || typeof discounted === "undefined") return true;
    if (isNaN(d) || d < 0) {
      dispatch(handleErrorMessage(t("errorMessageTranslations.enterValidNumber")));
      return false;
    }
    if (!isNaN(p) && p > 0 && d >= p) {
      dispatch(handleErrorMessage(t("courseMonetisation.discountMustBeLower") || "Discounted price must be lower than price"));
      return false;
    }
    return true;
  };

  return (
    <div className={styles.main}>
      {showConfirmModal && (
        <Modal
          card={
            <ConfirmAddContentModal
              onYes={() => {
                setIsLoadingNextBtn(true);
                handleNextAndSave("next");
              }}
              onLater={() => {
                setIsLoadingSaveBtn(true);
                handleNextAndSave("save");
              }}
            />
          }
          onOutsideClick={() => setShowConfirmModal(false)}
        />
      )}
      <IconTitle
        title={title}
        img="/Image/CourseMonetisation/booklet-line-2.svg"
        notClickable={true}
        type="ASSIGNMENT"
        style={{ fontWeight: 600, fontSize: "18px", fontFamily: "Lato" }}
      />
      <div className={styles.image_row}>
        <div className={styles.image_col}>
          <img src="/Image/CourseMonetisation/course-static-image.png" alt="" className={styles.static_image} />
        </div>
        <div className={styles.image_col}>
          {/* add thumbnail upload here */}
          <div className={styles.thumbnail_area}>
            <img src="/Image/CourseMonetisation/upload-cover-image.png" className={styles.thumbnail_cover}/>
            <div className={styles.upload_box}>
              <input
                onChange={(e) => {
                  handleThumbnail(e);
                }}
                type="file"
                accept="image/*"
                className={styles.file_upload}
                ref={uploadButtonRef}
              />
              <img
                src="/Image/CreateCourse/upload.svg"
                alt="Photos"
                className={styles.upload_img}
              />
              <p className={styles.upload_txt}>
                {t("createCourse.uploadThumbnail")}
              </p>
              <p className={styles.support_txt}>
                {t("createCourse.supportJpegPngFiles")}
              </p>
            </div>
            {thumbnail && (
              <img
                src={
                  typeof thumbnail === "string"
                    ? thumbnail
                    : URL.createObjectURL(thumbnail)
                }
                className={styles.thumbnail_pic}
              />
            )}

            {thumbnail && (
              <div
                className={styles.change_thumbnail}
                onClick={() => uploadButtonRef.current.click()}
              >
                <img
                  src={"/Image/DashImage/camera.svg"}
                  className={styles.camera_img}
                  alt=""
                  title={t("createCourse.changeThumbnail")}
                />
              </div>
            )}
          </div>
        </div>
      </div>

      {/* <div className={styles.input_label}>
        <span>{t("createCourse.publishTo")}*</span>
        <TooltipWrapper
          title={`${courseType == "Public" ? t("createCourse.publishPublicTooltip") : t("createCourse.publishPrivateTooltip")
            }`}
        >
          <img src="/Image/Icons/Iicon.svg" className={styles.tooltip_icon} />
        </TooltipWrapper>
      </div> */}

      {/* <MultipleSelectChip
        courseFeedCourse={props.courseFeedCourse}
        courseTitleType={courseTitleType}
        getSelectedClasses={getSelectedClasses}
        notDisabled="true"
        id="publishTo"
        classTags={currentCourse?.classes} // in case of Edit, the already selected classes.
        assignmentClass={classes} // classes is an array of all the classes where the logged-in user is the owner.
      /> */}

      <div className={styles.row}>
        <div className={styles.col}>
          <span className={styles.input_label}>
            {t("createCourse.courseName")}*
          </span>
          <input
            type="text"
            value={courseName}
            className={styles.input_field}
            placeholder={t("createCourse.courseNamePH")}
            onChange={(e) => {
              setCourseName(e.target.value);
            }}
          />
        </div>
        
        <div className={styles.col}>
          <span className={styles.input_label}>
            {t("createCourse.uploadPreviewVideo")}
            <img
              src={infoIconSrc}
              alt="Info"
              className={styles.info_icon}
              aria-label="Give learners a quick glimpse of your course."
              title="Give learners a quick glimpse of your course."
            />
          </span>

          {/* Upload preview control - now opens modal */}
          <div className={styles.uploadPreviewWrapper}>
            <div
              className={styles.previewBox}
              onClick={() => setShowPreviewModal(true)}
              role="button"
              tabIndex={0}
              aria-label="Open upload preview modal"
            >
              {previewFile ? (
                <div className={styles.previewInfo}>
                  <span className={styles.fileName}>{previewFile.name}</span>
                </div>
              ) : previewLink ? (
                <div className={styles.previewInfo}>
                  <span className={styles.fileName}>Link: {previewLink}</span>
                </div>
              ) : (
                <div className={styles.placeholder}>No file Selected</div>
              )}
            </div>

            <button
              type="button"
              className={styles.uploadBtn}
              onClick={() => setShowPreviewModal(true)}
              aria-label={previewFile ? "Change preview video" : "Upload preview video"}
            >
              {previewFile || previewLink ? "Change" : "Upload"}
            </button>

            {/* keep the existing hidden input (in case other code relies on it),
                but don't trigger it directly — modal will handle file selection */}
            <input
              ref={previewInputRef}
              type="file"
              accept="video/*"
              onChange={handlePreviewFile}
              className={styles.hiddenFileInput}
              style={{ display: "none" }}
            />
          </div>
        </div>
      </div>

      <div className={styles.row}>
        <div className={styles.col}>
          <span className={styles.input_label}>
            {t("createCourse.courseSubtitle")}*
          </span>
          <input
            type="text"
            value={courseSubtitle}
            className={styles.input_field}
            placeholder={t("createCourse.courseSubtitlePH")}
            onChange={(e) => {
              setCourseSubtitle(e.target.value);
            }}
          />
        </div>

        <div className={styles.col}>
          <span className={styles.input_label}>
            {t("createCourse.courseLevel")}*
          </span>
          <CustomSelect
            name="courseLevel"
            value={courseLevel}
            onChange={(e) => setCourseLevel(e.target.value)}
            placeholder={t("createCourse.levelPH")}
            options={[
              { value: "kids", label: "Kids Level" },
              { value: "beginner", label: "Beginner Level" },
              { value: "intermediate", label: "Intermediate Level" },
              { value: "expert", label: "Expert Level" },
              { value: "all", label: "All Levels" },
            ]}
          />
        </div>
      </div>

      {/* --- Prerequisites + Learning outcomes + Tags --- */}
      <div style={{ marginTop: 12 }}>
        <span className={styles.input_label}>
          {t("createCourse.prerequisites")}
          <img
            src={infoIconSrc}
            alt="Info"
            className={styles.info_icon}
            aria-label="Mention what learners should know or have beforehand."
            title="Mention what learners should know or have beforehand."
          />
        </span>

        {prerequisites.map((item, idx) => {
          // show delete icon when field has content OR total fields exceed minimum (1)
          const showDelete = (item || "").trim() !== "" || (prerequisites?.length || 0) > 1;
          return (
            <div key={`pre-${idx}`} style={{ marginTop: 8 }}>
              <div className={styles.input_with_delete}>
                <input
                  type="text"
                  value={item}
                  placeholder={t("createCourse.prerequisitesPH")}
                  className={styles.input_field}
                  onChange={(e) => updateArrayItem(setPrerequisites)(idx, e.target.value)}
                  aria-label={`${t("createCourse.prerequisites")} ${idx + 1}`}
                />

                {showDelete && (
                  <button
                    type="button"
                    className={styles.delete_icon_btn}
                    onClick={() => handleDeleteOrClear(prerequisites, setPrerequisites, idx, 1)}
                    aria-label={t("createCourse.removeField") || "Remove"}
                  >
                    <img className={styles.deleteIcon} src="/Image/CourseMonetisation/delete-icon.svg" alt="" />
                  </button>
                )}
              </div>
            </div>
          );
        })}

        <div style={{ marginTop: 8 }}>
          <button type="button" onClick={addArrayItem(setPrerequisites)} className={styles.add_more_btn}>
            + {t("createCourse.addMoreFields")}
          </button>
        </div>

        <div style={{ marginTop: 12 }}>
          <span className={styles.input_label}>
            {t("createCourse.learningOutcomes")}
            <img
              src={infoIconSrc}
              alt="Info"
              className={styles.info_icon}
              aria-label="List what learners will be able to do after completing your course"
              title="List what learners will be able to do after completing your course"
            />
          </span>

          {learningOutcomes.map((item, idx) => {
            // show delete icon when field has content OR total fields exceed minimum (4)
            const showDelete = (item || "").trim() !== "" || (learningOutcomes?.length || 0) > 4;
            return (
              <div key={`out-${idx}`} style={{ marginTop: 8 }}>
                <div className={styles.input_with_delete}>
                  <input
                    type="text"
                    value={item}
                    placeholder={t("createCourse.learningOutcomesPH")}
                    className={styles.input_field}
                    onChange={(e) => updateArrayItem(setLearningOutcomes)(idx, e.target.value)}
                    aria-label={`${t("createCourse.learningOutcomes")} ${idx + 1}`}
                  />

                  {showDelete && (
                    <button
                      type="button"
                      className={styles.delete_icon_btn}
                      onClick={() => handleDeleteOrClear(learningOutcomes, setLearningOutcomes, idx, 4)}
                      aria-label={t("createCourse.removeField") || "Remove"}
                    >
                      <img className={styles.deleteIcon} src="/Image/CourseMonetisation/delete-icon.svg" alt="" />
                    </button>
                  )}
                </div>
              </div>
            );
          })}

          <div style={{ marginTop: 8 }}>
            <button type="button" onClick={addArrayItem(setLearningOutcomes)} className={styles.add_more_btn}>
              + {t("createCourse.addMoreFields")}
            </button>
          </div>
        </div>

        <div style={{ marginTop: 12 }}>
          <span className={styles.input_label}>{t("createCourse.tags")}</span>

          <div className={styles.tags_input_box} role="group" aria-label={t("createCourse.tags")}>
            <div className={styles.tags_inner}>
              {/* left side: tags (chips) */}
              <div className={styles.tags_container} aria-live="polite">
                {tags.map((tg, i) => (
                  <div key={`tag-${i}`} className={styles.tag_chip}>
                    <span className={styles.tag_text}>{tg}</span>
                    <button
                      type="button"
                      className={styles.tag_remove_btn}
                      aria-label={`Remove ${tg}`}
                      onClick={() => removeTag(i)}
                    >
                      ×
                    </button>
                  </div>
                ))}
              </div>

              {/* right side: input that shrinks as tags grow */}
              <input
                type="text"
                value={tagInput}
                onChange={(e) => setTagInput(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    e.preventDefault();
                    addTag();
                  } else if (e.key === "Backspace" && tagInput === "" && tags.length) {
                    // quick UX: Backspace removes last tag when input empty
                    removeTag(tags.length - 1);
                  }
                }}
                onBlur={() => {
                  // optional: commit tag on blur
                  if (tagInput && tagInput.trim()) addTag();
                }}
                placeholder={t("createCourse.tagsPH")}
                className={styles.tag_input}
                aria-label={t("createCourse.tagsPH")}
              />
            </div>
          </div>
        </div>
      </div>

      {/* {!isFree && (
        <>
          {" "}
          <span className={styles.input_label}>{t("createCourse.price")}*</span>
          <input
            type="number"
            value={coursePriceValue}
            className={styles.input_field}
            placeholder={t("createCourse.pricePH")}
            onChange={(e) => setCoursePriceValue(e.target.value)}
          />
          <span className={styles.input_label}>
            {t("createCourse.discountedPrice")}*
          </span>
          <input
            type="number"
            value={courseDiscountedPrice}
            className={styles.input_field}
            placeholder={t("createCourse.discountedPricePH")}
            onChange={(e) => setCourseDiscountedPrice(e.target.value)}
          />
        </>
      )} */}
      <span className={styles.input_label}>
        {t("courseMonetisation.description")}*
      </span>
      <textarea
        rows="4"
        type="text"
        value={courseDescription}
        className={styles.textbox_field}
        placeholder={t("createCourse.descPH")}
        onChange={(e) => {
          setCourseDescription(e.target.value);
        }}
        defaultValue={props.courseDescription}
      />

      {/* Live class URL - only show for live */}
      {(courseMode === "live") && (
        <div className={styles.row}>
          <div className={styles.col}>
            <span className={styles.input_label}>
              {t("createCourse.liveSessionURL")}
              {requiresLiveUrl ? " *" : ""}
            </span>

            <div
              style={{
                display: "flex",
                alignItems: "center",
                // marginTop: "8px",
              }}
            >
              <div
                style={{
                  flex: 1,
                  display: "flex",
                  alignItems: "center",
                  border: "1px solid #E5E5E5",
                  borderRadius: 8,
                  padding: "0px 20px",
                  background: "#fff",
                  gap: 12,
                }}
              >
                <input
                  type="url"
                  className={styles.input_field}
                  placeholder={t("createCourse.liveSessionURLPH")}
                  value={liveClassUrl}
                  onChange={(e) => {
                    setLiveClassUrl(e.target.value);
                  }}
                  style={{
                    border: "none",
                    outline: "none",
                    padding: 0,
                    margin: 0,
                    flex: 1,
                    fontSize: "0.875rem",
                    lineHeight: 16,
                  }}
                />
                {/* Vertical separator line */}
                <div
                  style={{
                    width: "1px",
                    height: "36px",
                    backgroundColor: "#E5E5E5",
                  }}
                />

                {/* inside-right button area that looks like it's part of the input */}
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 8,
                  }}
                >
                  {!freqChanged ? (
                    <button
                      onClick={() => setShowFrequencyModal(true)}
                      style={{
                        display: "inline-flex",
                        fontFamily: "Lato",
                        alignItems: "center",
                        gap: 8,
                        border: "none",
                        background: "transparent",
                        cursor: "pointer",
                        padding: "0px 10px",
                        borderRadius: 20,
                        color: "#000000",
                        fontWeight: 300,
                      }}
                      aria-label="Add Frequency"
                    >
                      <span style={{ fontSize: 22,  color: "#F33070", fontWeight: 500 }}>＋</span>
                      <span style={{ fontSize: 14 }}>Add Frequency</span>
                    </button>
                  ):(
                    <button
                      onClick={() => setShowFrequencyModal(true)}
                      style={{
                        display: "inline-flex",
                        fontFamily: "Lato",
                        alignItems: "center",
                        gap: 8,
                        border: "none",
                        background: "transparent",
                        cursor: "pointer",
                        padding: "0px 10px",
                        borderRadius: 20,
                        color: "#000000",
                        fontWeight: 300,
                      }}
                      aria-label="Add Frequency"
                    >
                      <img src="/Image/CourseMonetisation/Change-frequency.svg" style={{ width: 14, height: 14 }}/>
                      <span style={{ fontSize: 14 }}>Change Frequency</span>
                    </button>
                  )}
                </div>
              </div>
            </div>
          </div>
          <div className={styles.col}>
            <span className={styles.input_label}>Timezone*</span>
            <CustomSelect
              name="timezone"
              value={timezone}
              onChange={(e) => setTimezone(e.target.value)}
              placeholder="Select timezone"
              options={[
                { value: "ist", label: "India Standard Time (IST / UTC+05:30)" },
                { value: "pt",  label: "Pacific Time (PT / PST / UTC-08:00)" },
                { value: "mt",  label: "Mountain Time (MT / MST / UTC-07:00)" },
                { value: "ct",  label: "Central Time (CT / CST / UTC-06:00)" },
                { value: "et",  label: "Eastern Time (ET / EST / UTC-05:00)" },
              ]}
            />
          </div>
        </div>
      )}

      {showFrequencyModal && (
        <Modal
          card={
            <div
              onClick={(e) => e.stopPropagation()}
              className={styles.freqModalCard}
            >
              {/* header */}
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
                <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                  <div style={{ color: "#F33070", fontWeight: 300, fontSize: 30 }}>＋</div>
                  <div style={{ fontWeight: 700, marginTop: 5 }}>Add Details</div>
                </div>
                <button
                  onClick={() => setShowFrequencyModal(false)}
                  style={{ border: "none", background: "transparent", fontSize: 18, cursor: "pointer", color: "#F33070" }}
                >
                  ✕
                </button>
              </div>

              <div style={{ color: "#333", marginBottom: 12, fontWeight: 600 }}>
                How often should this Live course repeat?
              </div>

              <div style={{ borderTop: "1px solid #eee", paddingTop: 12 }}>
                {/* One-time (Single session) */}
                <div style={{ padding: "12px 0", borderBottom: "1px solid #f5f5f5" }}>
                  <label className={styles.freqLabel} style={{ display: "flex", gap: 12, alignItems: "flex-start" }}>
                    <input
                      type="radio"
                      name="freq"
                      checked={frequencyType === "none"}
                      onChange={() => handleFrequencyChange("none")}
                    />
                    <div style={{ width: "100%" }}>
                      <div style={{ fontWeight: 600 }}>One-time — Single session on a specific date.</div>
                      {frequencyType === "none" && (
                        <div>
                          {/* Titles for row */}
                          <div className={styles.freqRow2} style={{ marginTop: 12 }}>
                            <div style={{ color: "#666", fontSize: 13 }}>Date</div>
                            <div style={{ color: "#666", fontSize: 13 }}>Start time</div>
                          </div>

                          <div className={styles.freqRow2} style={{ marginTop: 8 }}>
                            <input
                              type="date"
                              value={repeatEndDate}
                              disabled={frequencyType !== "none"}
                              onChange={(e) => updateRepeatEndDate(e.target.value)}
                              // visual state (disabled) handled by `disabled` + css
                              style={{
                                opacity: frequencyType === "none" ? 1 : 0.6,
                              }}
                          />

                            <input
                              type="time"
                              value={freqStartTime}
                              disabled={frequencyType !== "none"}
                              onChange={(e) => updateFreqStartTime(e.target.value)}
                              style={{
                                opacity: frequencyType === "none" ? 1 : 0.6,
                                pointerEvents: frequencyType === "none" ? "auto" : "none",
                              }}
                            />

                          </div>
                        </div>
                      )}
                    </div>
                  </label>
                </div>
                
                {/* Daily */}
                <div style={{ padding: "12px 0", borderBottom: "1px solid #f5f5f5" }}>
                  <label className={styles.freqLabel} style={{ display: "flex", gap: 12, alignItems: "flex-start" }}>
                    <input
                      type="radio"
                      name="freq"
                      checked={frequencyType === "daily"}
                      onChange={() => handleFrequencyChange("daily")}
                    />
                    <div style={{ width: "100%" }}>
                      <div style={{ fontWeight: 600 }}>Daily – Repeat every day at the same time.</div>
                      {frequencyType === "daily" && (
                        <div>
                          {/* Titles for row */}
                          <div className={styles.freqRow3} style={{ marginTop: 12 }}>
                            <div style={{ color: "#666", fontSize: 13 }}>Start time</div>
                            <div style={{ color: "#666", fontSize: 13 }}>Start From (optional)</div>
                            <div style={{ color: "#666", fontSize: 13 }}>End date (optional)</div>
                          </div>

                          {/* inputs row */}
                          <div className={styles.freqRow3} style={{ marginTop: 8 }}>
                            <input
                              type="time"
                              value={freqStartTime}
                              disabled={frequencyType !== "daily"}
                              onChange={(e) => updateFreqStartTime(e.target.value)}
                              style={{
                                opacity: frequencyType === "daily" ? 1 : 0.6,
                                pointerEvents: frequencyType === "daily" ? "auto" : "none",
                              }}
                            />

                            <input
                              type="date"
                              value={startDate}
                              disabled={frequencyType !== "daily"}
                              onChange={(e) => updateStartDate(e.target.value || null)}
                              style={{
                                opacity: frequencyType === "daily" ? 1 : 0.6,
                              }}
                            />

                            <input
                              type="date"
                              value={repeatEndDate}
                              disabled={frequencyType !== "daily"}
                              onChange={(e) => updateRepeatEndDate(e.target.value || null)}
                              style={{
                                opacity: frequencyType === "daily" ? 1 : 0.6,
                              }}
                            />
                          </div>
                        </div>
                      )}
                    </div>
                  </label>
                </div>

                {/* Weekly */}
                <div style={{ padding: "12px 0", borderBottom: "1px solid #f5f5f5" }}>
                  <label className={styles.freqLabel} style={{ display: "flex", gap: 12, alignItems: "flex-start" }}>
                    <input
                      type="radio"
                      name="freq"
                      checked={frequencyType === "weekly"}
                      onChange={() => handleFrequencyChange("weekly")}
                    />
                    <div style={{ width: "100%" }}>
                      <div style={{ fontWeight: 600 }}>Weekly – Repeat on selected days of the week.</div>
                      {frequencyType === "weekly" && (
                        <div>
                          <div style={{ marginTop: 8, color: "#666", fontSize: 13 }}>Choose days for your Course:</div>

                          <div className={styles.daysGrid} style={{ marginTop: 8 }}>
                            {["monday","tuesday","wednesday","thursday","friday","saturday","sunday"].map((d) => (
                              <label
                                key={d}
                                style={{
                                  display: "flex",
                                  gap: 6,
                                  alignItems: "center",
                                  opacity: frequencyType === "weekly" ? 1 : 0.5,
                                  pointerEvents: frequencyType === "weekly" ? "auto" : "none",
                                }}
                              >
                                <input
                                  type="checkbox"
                                  checked={!!freqWeeklyDays[d]}
                                  disabled={frequencyType !== "weekly"}
                                  onChange={() => toggleWeeklyDay(d)}
                                />
                                <span style={{ textTransform: "capitalize", fontSize: 13 }}>{d.charAt(0).toUpperCase() + d.slice(1)}</span>
                              </label>
                            ))}
                          </div>

                          {/* Titles */}
                          <div className={styles.freqRow3} style={{ marginTop: 12 }}>
                            <div style={{ color: "#666", fontSize: 13 }}>Start time</div>
                            <div style={{ color: "#666", fontSize: 13 }}>Start From (optional)</div>
                            <div style={{ color: "#666", fontSize: 13 }}>End date (optional)</div>
                          </div>

                          <div className={styles.freqRow3} style={{ marginTop: 8 }}>
                            <input
                              type="time"
                              value={freqStartTime}
                              disabled={frequencyType !== "weekly"}
                              onChange={(e) => updateFreqStartTime(e.target.value)}
                              style={{
                                opacity: frequencyType === "weekly" ? 1 : 0.6,
                                pointerEvents: frequencyType === "weekly" ? "auto" : "none",
                              }}
                            />

                            <input
                              type="date"
                              value={startDate}
                              disabled={frequencyType !== "weekly"}
                              onChange={(e) => updateStartDate(e.target.value || null)}
                              style={{
                                opacity: frequencyType === "weekly" ? 1 : 0.6,
                              }}
                            />

                            <input
                              type="date"
                              value={repeatEndDate}
                              disabled={frequencyType !== "weekly"}
                              onChange={(e) => updateRepeatEndDate(e.target.value || null)}
                              style={{
                                opacity: frequencyType === "weekly" ? 1 : 0.6,
                              }}
                            />
                          </div>
                        </div>
                      )}
                    </div>
                  </label>
                </div>

                {/* Monthly */}
                <div style={{ padding: "12px 0", borderBottom: "1px solid #f5f5f5" }}>
                  <label className={styles.freqLabel} style={{ display: "flex", gap: 12, alignItems: "flex-start" }}>
                    <input
                      type="radio"
                      name="freq"
                      checked={frequencyType === "monthly"}
                      onChange={() => handleFrequencyChange("monthly")}
                    />
                    <div style={{ width: "100%" }}>
                      <div style={{ fontWeight: 600 }}>Monthly – Repeat on a specific date each month.</div>
                      {frequencyType === "monthly" && (
                        <div>
                          {/* Titles */}
                          <div className={styles.freqRow3} style={{ marginTop: 12 }}>
                            <div style={{ color: "#666", fontSize: 13 }}>Select Date of Month:</div>
                            <div style={{ color: "#666", fontSize: 13 }}>Start time</div>
                            <div style={{ color: "#666", fontSize: 13 }}>Start From (optional)</div>
                          </div>

                          <div className={styles.freqRow3} style={{ marginTop: 8 }}>
                            <input
                              type="date"
                              value={freqMonthlyDate}
                              disabled={frequencyType !== "monthly"}
                              onChange={(e) => updateFreqMonthlyDate(e.target.value)}
                              style={{
                                opacity: frequencyType === "monthly" ? 1 : 0.6,
                              }}
                            />

                            <input
                              type="time"
                              value={freqStartTime}
                              disabled={frequencyType !== "monthly"}
                              onChange={(e) => updateFreqStartTime(e.target.value)}
                              style={{
                                opacity: frequencyType === "monthly" ? 1 : 0.6,
                                pointerEvents: frequencyType === "monthly" ? "auto" : "none",
                              }}
                            />

                            <input
                              type="date"
                              value={startDate}
                              disabled={frequencyType !== "monthly"}
                              onChange={(e) => updateStartDate(e.target.value || null)}
                              style={{
                                opacity: frequencyType === "monthly" ? 1 : 0.6,
                              }}
                            />
                          </div>
                          <div className={styles.freqRow3} style={{ marginTop: 12 }}>
                            <div style={{ color: "#666", fontSize: 13 }}>End date (optional)</div>
                          </div>
                          <div className={styles.freqRow3} style={{ marginTop: 12 }}>
                            <input
                              type="date"
                              value={repeatEndDate}
                              disabled={frequencyType !== "monthly"}
                              onChange={(e) => updateRepeatEndDate(e.target.value || null)}
                              style={{
                                opacity: frequencyType === "monthly" ? 1 : 0.6,
                              }}
                            />
                          </div>
                        </div>
                      )}
                    </div>
                  </label>
                </div>
              </div>

              {/* modal buttons */}
              <div style={{ display: "flex", justifyContent: "flex-end", gap: 12, marginTop: 18 }}>
                <button
                  onClick={() => setShowFrequencyModal(false)}
                  style={{
                    padding: "8px 20px",
                    borderRadius: 26,
                    border: "1px solid #F33070",
                    background: "#ffffff",
                    color: "#F33070",
                    cursor: "pointer",
                    fontWeight: 700,
                    fontSize: 13,
                    letterSpacing: "0.6px",
                    textTransform: "uppercase",
                    outline: "none",
                    boxShadow: "none",
                  }}
                  aria-label="Back"
                >
                  BACK
                </button>

                <button
                  onClick={() => {
                    setShowFrequencyModal(false);
                  }}
                  style={{
                    padding: "8px 20px",
                    borderRadius: 26,
                    border: "1px solid #F33070",
                    background: "#ffffff",
                    color: "#F33070",
                    cursor: "pointer",
                    fontWeight: 700,
                    fontSize: 13,
                    letterSpacing: "0.6px",
                    textTransform: "uppercase",
                    outline: "none",
                    boxShadow: "none",
                  }}
                  aria-label="Continue"
                >
                  CONTINUE
                </button>
              </div>

            </div>
          }
          onOutsideClick={() => setShowFrequencyModal(false)}
        />
      )}

      {/* Signature area */}
      {/* <div style={{ marginTop: 20 }}> */}
        <span className={styles.input_label}>{t("createCourse.courseSignature")}*</span>
        <input
          type="text"
          className={styles.input_field}
          placeholder={t("createCourse.courseSignaturePH")}
          onClick={() => setShowSignatureModal(true)}
        />
        {signature && (
          <div style={{ display: "flex", gap: 12, alignItems: "center" }}>
            <div
              className={styles.signaturePreviewBox}
              onClick={() => setShowSignatureModal(true)}
              role="button"
              tabIndex={0}
              aria-label="Add signature"
            >
              <img
                src={typeof signature === "string" ? signature : URL.createObjectURL(signature)}
                alt="signature"
                className={styles.signatureImg}
              />

              <button
                type="button"
                className={styles.sigClearXBtn}
                aria-label="Remove signature"
                onClick={(e) => {
                  e.stopPropagation();
                  setSignature(null);
                  setSignatureFile(null);
                }}
              >
                ×
              </button>
            </div>
          </div>
        )}
      {/* </div> */}

      {showSignatureModal && (
        <Modal
          card={<SignatureModal
            onClose={() => setShowSignatureModal(false)}
            onSave={(dataUrl, file) => {
              setSignature(dataUrl);
              setSignatureFile(file || null);
            }}
          />}
          onOutsideClick={() => setShowSignatureModal(false)}
        />
      )}

      {showPreviewModal && (
        <div
          onClick={() => setShowPreviewModal(false)}
          style={{
            position: "fixed",
            inset: 0,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            background: "rgba(0,0,0,0.5)",
            zIndex: 2000,
          }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              width: 440,
              maxWidth: "94vw",
              // minHeight: 350,
              borderRadius: 8,
              background: "#ffffff",           // solid white background — stops transparency
              boxShadow: "0 12px 40px rgba(0,0,0,0.25)", // depth
              zIndex: 2010,
            }}
          >
            <div onClick={(e) => e.stopPropagation()} style={{ width: 440, maxWidth: "94vw", minHeight: 350 }}>
              {/* header */}
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", paddingTop:14, paddingBottom:12, paddingRight:18, paddingLeft:18 }}>
                <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                  <div style={{ fontWeight: 500, fontSize: 14 }}>Upload Preview Video</div>
                </div>
                <button
                  onClick={() => setShowPreviewModal(false)}
                  style={{ border: "none", background: "transparent", fontSize: 18, cursor: "pointer", color: "#F33070", fontWeight: 700}}
                  aria-label="Close"
                >
                  ✕
                </button>
              </div>

              <hr style={{ margin:0, opacity: 0.15 }} />

              {/* body */}
              <div style={{ display: "flex", flexDirection: "column", gap: 16, padding:20 }}>
                {/* dotted drop area */}
                <div
                  role="button"
                  tabIndex={0}
                  onClick={() => modalFileInputRef.current?.click()}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") modalFileInputRef.current?.click();
                  }}
                  onDrop={(e) => {
                    e.preventDefault();
                    const f = e.dataTransfer.files?.[0];
                    if (f) handleModalFilePicked(f);
                  }}
                  onDragOver={(e) => e.preventDefault()}
                  style={{
                    border: "2px dashed #D5D5D5",
                    borderRadius: 8,
                    padding: 8,
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "center",
                    textAlign: "center",
                    height: 120,
                    background: "#fff",
                    cursor: "pointer",
                    outline: "none",
                  }}
                >
                  <img src="/Image/CreateCourse/upload-cloud-icon.svg" alt="upload" style={{ width: 22, height: 23 }} />
                  <div style={{ gap: 2 }}>
                    <div style={{ color: "#484848", fontWeight: 400, fontSize: 13 }}>Click or drag a video to this area to upload</div>
                    <div style={{ color: "#454545", fontWeight: 300, fontSize: 11 }}>Format accepted .mp4 (25mb)</div>
                  </div>
                  <input
                    ref={modalFileInputRef}
                    type="file"
                    accept="video/mp4,video/*"
                    style={{ display: "none" }}
                    id="modalPreviewFileInput"
                    onChange={(e) => {
                      const f = e.target.files?.[0];
                      handleModalFilePicked(f);
                      e.target.value = "";
                    }}
                  />
                </div>

                {modalFile && (
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      width: "100%",
                      padding: "6px 8px",
                      backgroundColor: "#F6F6F6",
                      borderRadius: "8px", // Pill shape
                      boxSizing: "border-box"
                    }}
                  >
                    <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          width: "32px",
                          height: "32px",
                          borderRadius: "50%",
                          color: "white",
                          fontSize: "18px"
                        }}
                      >
                        <img src="/Image/CourseMonetisation/video-icon.svg" alt="play" style={{ width: "16px", height: "16px" }} />
                      </div>
                      <span style={{ fontSize: "12px", color: "#484848", fontWeight: 500 }}>
                        {modalFile.name}
                      </span>
                    </div>

                    {/* Right side: Delete Button */}
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        setModalFile(null);
                      }}
                      style={{
                        background: "none",
                        border: "none",
                        cursor: "pointer",
                        padding: "4px",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        fontSize: "20px"
                      }}
                      aria-label="Remove file"
                    >
                      <img src="/Image/CourseMonetisation/delete-icon-pink.svg" alt="delete" style={{ width: "16px", height: "16px" }} />
                    </button>
                  </div>
                )}

                {/* divider "OR add video link" */}
                <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                  <div style={{ flex: 1, height: 1, background: "#EDEDED" }} />
                  <div style={{ padding: "0 8px", color: "#454545", fontSize: 11, fontWeight: 300 }}>OR add video link</div>
                  <div style={{ flex: 1, height: 1, background: "#EDEDED" }} />
                </div>

                {/* link input row */}
                <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                  <div>
                    <input
                      type="text"
                      value="https://"
                      disabled
                      style={{
                        border: "1px solid #E5E5E5",
                        borderRadius: 8,
                        width: 58,
                        borderTopRightRadius:0,
                        borderBottomRightRadius:0,
                        borderRight: "none",
                        padding: "6px 8px",
                        background: "#FFF",
                        fontSize: 11,
                        fontWeight: 300,
                        color: "#000",
                        pointerEvents: "none",
                      }}
                    />
                    <input
                      type="text"
                      placeholder="Example - youtu.be/FTFaQWZBqQ8"
                      value={modalLink}
                      className={styles.video_link_input}
                      onChange={(e) => setModalLink(e.target.value)}
                      style={{
                        flex: 1,
                        width: "258px",
                        border: "1px solid #E5E5E5",
                        borderRadius: 8,
                        borderTopLeftRadius:0,
                        borderBottomLeftRadius:0,
                        padding: "6px 8px",
                        outline: "none",
                        fontSize: 11,
                        fontWeight: 300,
                        color: "#000",
                      }}
                    />
                  </div>
                  <button
                    type="button"
                    onClick={() => {
                      if (modalLink && modalLink.trim() !== "") {
                        setPreviewLink(normalizeUrl(modalLink));
                        setModalFile(null);
                      } else {
                        // no link -> do nothing (optionally show validation)
                      }
                    }}
                    style={{
                      padding: "6px 8px",
                      borderRadius: 8,
                      width: "73px",
                      border: "1px solid #E5E5E5",
                      background: "#FFF",
                      fontSize: 11,
                      fontWeight: 500,
                      color: "#000",
                      cursor: "pointer",
                      marginLeft: 3,
                    }}
                  >
                    Upload
                  </button>
                </div>
              </div>

              <hr style={{ margin:0, opacity: 0.15 }} />

              {/* footer buttons */}
              <div style={{ display: "flex", justifyContent: "flex-end", gap: 12, padding: 20, paddingTop: 9, paddingBottom: 9 }}>
                <button
                  onClick={() => {
                    // cancel modal and clear temp state
                    setModalFile(null);
                    setModalLink("");
                    setShowPreviewModal(false);
                  }}
                  style={{
                    padding: "8px 16px",
                    borderRadius: 8,
                    border: "1px solid #CBCBCB",
                    background: "#ffffff",
                    color: "#232222",
                    cursor: "pointer",
                    fontWeight: 400,
                    fontSize: 12.6,
                    outline: "none",
                  }}
                >
                  Cancel
                </button>

                <button
                  onClick={() => {
                    // continue: prefer uploaded file, else use entered link
                    if (modalFile) {
                      setPreviewFile(modalFile);
                      setPreviewLink("");
                    } else if (modalLink && modalLink.trim() !== "") {
                      setPreviewLink(normalizeUrl(modalLink));
                      setPreviewFile(null);
                    }
                    // clear modal temp state and close
                    setModalFile(null);
                    setModalLink("");
                    setShowPreviewModal(false);
                  }}
                  style={{
                    padding: "8px 16px",
                    borderRadius: 8,
                    border: "none",
                    background: "#F33070",
                    color: "#ffffff",
                    cursor: "pointer",
                    fontWeight: 400,
                    fontSize: 12.6,
                    outline: "none",
                  }}
                  aria-label="Continue"
                >
                  Continue
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      <div className={styles.radio_group}>
        <FormControl>
          <RadioGroup
            row
            aria-labelledby="demo-row-radio-buttons-group-label"
            name="row-radio-buttons-group"
            defaultValue={
              courseTitleType == "Edit"
                ? currentCourse?.free
                  ? "free"
                  : "paid"
                : "free"
            }
            onChange={(e) => {
              setIsFree(e.target.value == "free");
            }}
          >
            <FormControlLabel
              sx={{ fontFamily: "Euclid Square" }}
              disabled={courseTitleType == "Edit"}
              value={"free"}
              control={
                <Radio
                  size="small"
                  sx={{
                    "&.Mui-checked": { color: "#F33070" },
                    marginRight: "0px",
                    fontFamily: "Euclid Square",
                    fontWeight: 400,
                    fontSize: "16px",
                    "&.Mui-disabled": {
                      color: "#959595",
                    },
                  }}
                />
              }
              label={t("common.free")}
            />

            <FormControlLabel
              sx={{ fontFamily: "Euclid Square" }}
              disabled={courseTitleType == "Edit"}
              value={"paid"}
              control={
                <Radio
                  size="small"
                  sx={{
                    "&.Mui-checked": { color: "#F33070" },
                    marginRight: "0px",
                    fontFamily: "Euclid Square",
                    fontWeight: 400,
                    fontSize: "16px",
                    "&.Mui-disabled": {
                      color: "#959595",
                    },
                  }}
                />
              }
              label={t("common.paid")}
            />
          </RadioGroup>
        </FormControl>
      </div>
      {!isFree && (
        <div>
          <h3 for="description" className={styles.input_label}>
            {t("createCourse.price")}*
          </h3>
          <div className={styles.price_div}>
            <input
              type="text"
              id="priceType"
              className={styles.price_type_input}
              value={`${t("createCourse.USD")}`}
              disabled
              style={{
                opacity: 1,              // Prevent graying out
                color: "#000",           // Ensure normal text color
                cursor: "default",       // Use default cursor (or pointer)
                backgroundColor: "#fff", // Keep background normal
                pointerEvents: "none",   // Ensure no interaction (optional)
              }}
            />
            <input
              type="number"
              id="price"
              onChange={(e) => {
                const newPrice = e.target.value === "" ? "" : Number(e.target.value);
                setCoursePriceValue(newPrice);

                const currentDiscount = Number(courseDiscountedPrice);
                if (newPrice !== "" && !isNaN(currentDiscount) && currentDiscount >= Number(newPrice) && Number(newPrice) > 0) {
                  const fallback = Number(newPrice) > 1 ? Number(newPrice) - 1 : 0;
                  setCourseDiscountedPrice(fallback);
                }
              }}
              className={styles.price_input}
              placeholder={"Enter Price"}
              value={coursePriceValue}
            />
            <input
              type="number"
              id="discounted_price"
              onChange={(e) => {
                const newDiscountRaw = e.target.value;
                const newDiscount = newDiscountRaw === "" ? "" : Number(newDiscountRaw);
                const price = Number(coursePriceValue);

                if (newDiscount !== "" && !isNaN(price) && price > 0 && newDiscount >= price) {
                  const fallback = price > 1 ? price - 1 : 0;
                  setCourseDiscountedPrice(fallback);
                } else {
                  setCourseDiscountedPrice(newDiscount);
                }
              }}
              className={styles.price_input}
              placeholder={"Enter Discounted Price"}
              value={courseDiscountedPrice}
            />
          </div>
          {/* Payment Link */}
          <div style={{ marginTop: "12px" }}>
            <input
              type="url"
              className={styles.input_field}
              placeholder="Payment link"
              value={paymentLink}
              onChange={(e) => {
                setPaymentLink(e.target.value);
              }}
              style={{
                borderRadius: "8px",
                border: "1px solid #E5E5E5",
              }}
            />
          </div>
        </div>
      )}

      <div className={styles.btn_div}>
        {courseTitleType === "Edit" ? (
          <button
            type="button"
            className={styles.btn}
            disabled={isLoadingNextBtn || isLoadingSaveBtn}
            aria-disabled={isLoadingNextBtn || isLoadingSaveBtn}
            onClick={() => {
              setIsLoadingNextBtn(true);
              handleNextAndSave("next");
            }}
          >
            {isLoadingNextBtn ? (
              <CircularProgress color="inherit" size={20} />
            ) : (
              "Update"
            )}
          </button>
        ) : (
          <button
            type="button"
            className={styles.btn}
            disabled={isBtnDisabled || isLoadingNextBtn || isLoadingSaveBtn}
            aria-disabled={isBtnDisabled || isLoadingNextBtn || isLoadingSaveBtn}
            onClick={() => {
              setShowConfirmModal(true);
            }}
          >
            {isLoadingNextBtn ? (
              <CircularProgress color="inherit" size={20} />
            ) : (
              t("common.next")
            )}
          </button>
        )}
      </div>
    </div>
  );
};

export default CreateCoursePage;
